import { useResizeObserver } from "@mantine/hooks";
import { useState, useEffect, useRef } from "react";
import { SessionResults } from "./BilanResultTabs";

type MaillageType = number[][];

interface CanvasGridProps {
  width: number;
  height: number;
  maillage: MaillageType;
  removedRows?: number[]; // rows we want in pink
  removedColumns?: number[]; // columns we want in pink
}

const CanvasGrid = ({
  width,
  height,
  maillage,
  removedRows = [],
  removedColumns = [],
}: CanvasGridProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext("2d");
    if (!context) return;

    const cellHeight = height / 12;
    const cellWidth = cellHeight * (5.5 / 4); // Width based on height with ratio 5.5/4

    // 2. Clear canvas before drawing
    context.clearRect(0, 0, width, height);

    // 3. Draw grid
    maillage.forEach((row, rowIndex) => {
      row.forEach((cell, colIndex) => {
        // Check if this cell is in a removed row or column
        const isRemovedRow = removedRows.includes(rowIndex);
        const isRemovedCol = removedColumns.includes(colIndex);

        if (isRemovedRow || isRemovedCol) {
          // Cells in removed row/col -> PINK
          context.fillStyle = "#006599";
        } else {
          // Otherwise, color based on cell value
          context.fillStyle =
            cell === 0
              ? "black"
              : cell === 1
              ? "darkgray"
              : cell === 2
              ? "lightgray"
              : cell === 3
              ? "white"
              : "red";
        }

        // Fill the cell
        context.fillRect(
          colIndex * cellWidth,
          rowIndex * cellHeight,
          cellWidth,
          cellHeight
        );

        // Outline grid lines
        context.strokeStyle = "black";
        context.lineWidth = 1;
        context.strokeRect(
          colIndex * cellWidth,
          rowIndex * cellHeight,
          cellWidth,
          cellHeight
        );

        // 4. Decide which text color to use (just for visibility)
        if (isRemovedRow || isRemovedCol) {
          context.fillStyle = "#006599";
        } else {
          context.fillStyle = "black";
        }

        // Write text in the middle of the cell
        context.font = "bold 16px Arial";
        const text = String(cell);
        const textWidth = context.measureText(text).width;
        const textX = colIndex * cellWidth + (cellWidth - textWidth) / 2;
        const textY = rowIndex * cellHeight + cellHeight / 2 + 6;
        context.fillText(text, textX, textY);
      });
    });

    // 5. Draw the central cross
    const totalGridWidth = 16 * cellWidth;
    const centerX = totalGridWidth / 2;
    const centerY = height / 2;
    const crossLength = Math.min(cellWidth, cellHeight) * 0.5;

    context.beginPath();
    context.strokeStyle = "red";
    context.lineWidth = 2;
    context.moveTo(centerX - crossLength / 2, centerY);
    context.lineTo(centerX + crossLength / 2, centerY);
    context.stroke();

    context.beginPath();
    context.moveTo(centerX, centerY - crossLength / 2);
    context.lineTo(centerX, centerY + crossLength / 2);
    context.stroke();
  }, [maillage, width, height, removedRows, removedColumns]);

  return (
    <canvas
      ref={canvasRef}
      width={16 * (height / 12) * (5.5 / 4)}
      height={height}
      style={{ display: "block" }}
    />
  );
};

export default CanvasGrid;

export const ResultsBilanDisplayV2 = ({
  resultData,
}: {
  resultData: SessionResults;
}) => {
  const [ref, rect] = useResizeObserver();

  const [maillage, setMaillage] = useState(
    [...Array(12)].map(() => Array(16).fill(0))
  );

  const convertPositionToIndices = (position: string) => {
    const letter = position.charAt(0);
    const number = parseInt(position.slice(1), 10);
    const col = letter.charCodeAt(0) - 65; // Convert letter to column index (A=0, B=1, ..., O=14, P=15)
    const row = number - 1; // Convert number to row index (1=0, 2=1, ..., 12=11)
    return { row, col };
  };

  useEffect(() => {
    if (resultData) {
      let jsonResults;

      try {
        if (resultData.resultsBilan || resultData.resultsBilan !== "null") {
          jsonResults = JSON.parse(resultData.resultsBilan);
        } else if (
          resultData.resultsBilan === null ||
          resultData.resultsBilan === "null"
        ) {
          jsonResults = null;
        }

        if (jsonResults) {
          // It's a maillage file
          const newMaillage = [...Array(12)].map(() => Array(16).fill(0));
          if (jsonResults[0].reactionTime) {
            jsonResults.forEach(
              (result: { position: string; reactionTime: string | number }) => {
                if (result.reactionTime !== "Trop long") {
                  const { row, col } = convertPositionToIndices(
                    result.position
                  );
                  newMaillage[row][col] += 1;
                }
              }
            );
          } else {
            jsonResults.forEach(
              (result: { position: string; successCount: string }) => {
                const { row, col } = convertPositionToIndices(result.position);
                newMaillage[row][col] = result.successCount; // Directly assign successCount to the cell
              }
            );
          }
          setMaillage(newMaillage);
        } else {
          // Clear the maillage and selectedCells
          setMaillage([...Array(12)].map(() => Array(16).fill(0)));
        }
      } catch (error) {
        console.error("Failed to parse JSON data:", error);
      }
    }
  }, [resultData]);

  const maxWidth = rect.width > 1400 ? 1400 : rect.width;

  return (
    <div ref={ref}>
      <CanvasGrid
        width={maxWidth}
        height={maxWidth * 0.55}
        maillage={maillage}
        removedRows={resultData.removedRows}
        removedColumns={resultData.removedColumns}
      />
    </div>
  );
};
